
import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Home, Key } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const PasswordReset = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { language, t } = useLanguage();
  
  const isResetMode = searchParams.has('access_token');

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        toast.error(t('reset_request_failed', 'فشل في إرسال طلب إعادة تعيين كلمة المرور', 'Failed to send password reset request'));
      } else {
        toast.success(t('reset_email_sent', 'تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني', 'Password reset link sent to your email'));
      }
    } catch (error) {
      toast.error(t('reset_error', 'خطأ في إعادة تعيين كلمة المرور', 'Password reset error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error(t('passwords_not_match', 'كلمات المرور غير متطابقة', 'Passwords do not match'));
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        toast.error(t('password_update_failed', 'فشل في تحديث كلمة المرور', 'Failed to update password'));
      } else {
        toast.success(t('password_updated', 'تم تحديث كلمة المرور بنجاح', 'Password updated successfully'));
      }
    } catch (error) {
      toast.error(t('password_update_error', 'خطأ في تحديث كلمة المرور', 'Password update error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center bg-background ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="w-full max-w-md space-y-8 p-6">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
              <Home className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold">{t('maskan', 'مسكن', 'Maskan')}</span>
          </Link>
        </div>

        <Card>
          <CardHeader className="text-center">
            <Key className="h-16 w-16 text-primary mx-auto mb-4" />
            <CardTitle>
              {isResetMode 
                ? t('reset_password', 'إعادة تعيين كلمة المرور', 'Reset Password')
                : t('forgot_password', 'نسيت كلمة المرور', 'Forgot Password')
              }
            </CardTitle>
            <CardDescription>
              {isResetMode
                ? t('enter_new_password', 'أدخل كلمة المرور الجديدة', 'Enter your new password')
                : t('enter_email_reset', 'أدخل بريدك الإلكتروني لإرسال رابط إعادة التعيين', 'Enter your email to receive a reset link')
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isResetMode ? (
              <form onSubmit={handlePasswordUpdate} className="space-y-4">
                <div>
                  <Label htmlFor="newPassword">{t('new_password', 'كلمة المرور الجديدة', 'New Password')}</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <div>
                  <Label htmlFor="confirmPassword">{t('confirm_password', 'تأكيد كلمة المرور', 'Confirm Password')}</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? t('updating', 'جاري التحديث...', 'Updating...') : t('update_password', 'تحديث كلمة المرور', 'Update Password')}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div>
                  <Label htmlFor="email">{t('email', 'البريد الإلكتروني', 'Email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? t('sending', 'جاري الإرسال...', 'Sending...') : t('send_reset_link', 'إرسال رابط إعادة التعيين', 'Send Reset Link')}
                </Button>
              </form>
            )}
            <div className="mt-4 text-center">
              <Link to="/signin" className="text-sm text-primary hover:underline">
                {t('back_to_signin', 'العودة لتسجيل الدخول', 'Back to Sign In')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PasswordReset;
